/**
 * S4 · The numbers grid, with the why rail and drafts.
 *
 * Edit in the table the numbers live in: click a cell, type, tab on (D44).
 * Nothing is live on keypress — the bar at the top counts the drafts and
 * publishing is a deliberate press (D45). The rail explains whichever cell is
 * selected: computed against served, how far it is caught up, and its history.
 *
 * Blue is the engine, orange means a person decided. A locked row is striped
 * and refuses the keyboard (D49).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { backToStats, loadTotals, type Totals } from "@/lib/statsEngine/api";
import {
  REASON_LABEL,
  draftEdits,
  listChanges,
  publishEdits,
  releaseEdits,
  type Change,
  type ReasonType,
} from "@/lib/statsEngine/authoring";
import {
  Button,
  Card,
  Empty,
  Field,
  Input,
  LinkButton,
  PageHead,
  Pill,
  Problem,
  Section,
  Select,
  Split,
  Table,
  Td,
  Th,
  Toolbar,
  Stack,
} from "@/components/kit";
import { useApi } from "@/spa/useApi";

import styles from "./NumbersGrid.module.css";

type Draft = { rowId: number; field: string; value: number; was: unknown };

function text(value: unknown): string {
  if (value === null || value === undefined) return "—";
  return String(value);
}

export function StatsNumbersPage() {
  const { sport = "" } = useParams<{ sport: string }>();
  const [params] = useSearchParams();
  const key = params.get("key") ?? "";
  const kind = params.get("kind") ?? "node";
  const ref = params.get("ref") ?? "";

  const load = useCallback(() => loadTotals(key, kind, ref), [key, kind, ref]);
  const { data, error, reload } = useApi<Totals>(
    load,
    [key, kind, ref],
    "the admin API is not reachable",
  );

  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [selected, setSelected] = useState<{ rowId: number; field: string } | null>(null);
  const [history, setHistory] = useState<Change[]>([]);
  const [reason, setReason] = useState<ReasonType>("ruling");
  const [note, setNote] = useState("");
  const [said, setSaid] = useState("");
  const [busy, setBusy] = useState(false);

  const columns = useMemo(() => (data ? Object.keys(data.fields) : []), [data]);
  const gives = data ? data.gives : "total";

  useEffect(() => {
    if (!key || !ref) return;
    let cancelled = false;
    listChanges(key, kind, ref)
      .then((rows) => {
        if (!cancelled) setHistory(rows);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [key, kind, ref, data]);

  const draftFor = useCallback(
    (rowId: number, field: string) => drafts.find((d) => d.rowId === rowId && d.field === field),
    [drafts],
  );

  const type = useCallback((rowId: number, field: string, was: unknown, typed: string) => {
    const value = Number(typed);
    setDrafts((current) => {
      const rest = current.filter((d) => !(d.rowId === rowId && d.field === field));
      if (typed.trim() === "" || Number.isNaN(value) || value === Number(was)) return rest;
      return [...rest, { rowId, field, value, was }];
    });
  }, []);

  const publish = useCallback(async () => {
    if (drafts.length === 0) return;
    if (!note.trim()) {
      setSaid("Every edit needs one line of why.");
      return;
    }
    setBusy(true);
    setSaid("");
    try {
      const batch = await draftEdits({
        targetTable: gives === "table" ? "table" : "total",
        rows: drafts.map((d) => ({ targetId: d.rowId, field: d.field, kind: "set", value: d.value })),
        reasonType: reason,
        note,
        author: "admin",
      });
      const done = await publishEdits(batch.batchId, "admin");
      setSaid(`Published. ${done.rows} row(s) changed, ${done.changes} number(s) moved.`);
      setDrafts([]);
      setNote("");
      reload();
    } catch (cause) {
      setSaid(cause instanceof Error ? cause.message : "could not publish");
    } finally {
      setBusy(false);
    }
  }, [gives, drafts, note, reason, reload]);

  const undo = useCallback(
    async (batchId: string) => {
      setBusy(true);
      try {
        await releaseEdits(batchId, "admin");
        setSaid("Undone. The engine's number is back.");
        reload();
      } catch (cause) {
        setSaid(cause instanceof Error ? cause.message : "could not undo");
      } finally {
        setBusy(false);
      }
    },
    [reload],
  );

  const selectedRow = data?.rows.find((r) => Number(r.id) === selected?.rowId);
  const selectedHistory = history.filter(
    (h) => !selected || (selectedRow && h.subjectId === String(selectedRow.subject_id)),
  );


  if (error) return <Problem>Could not reach the admin service: {error}</Problem>;

  return (
    <Stack>
      <PageHead
        title={data ? data.name : "Numbers"}
        note="What the engine worked out, and what is served. Click a number and type to change it — nothing is live until you publish."
        actions={
          <Toolbar>
            <LinkButton to={backToStats(sport, kind, ref)}>Back to Stats</LinkButton>
          </Toolbar>
        }
      />

      {said ? <Card>{said}</Card> : null}

      {/* The drafts bar. Nothing is live on a keypress: the count, the reason
          and the one line of why all sit together, and publishing is one
          deliberate press. */}
      {drafts.length > 0 ? (
        <Card>
          <Toolbar>
            <Pill tone="warn" dot>
              {drafts.length} unpublished change{drafts.length === 1 ? "" : "s"}
            </Pill>
            <Field label="Why">
              <Select value={reason} onChange={(e) => setReason(e.target.value as ReasonType)}>
                {Object.entries(REASON_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="One line of why">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="two runs struck off by the match referee"
              />
            </Field>
            <Button tone="primary" disabled={busy} onClick={publish}>
              Publish edits
            </Button>
            <Button disabled={busy} onClick={() => setDrafts([])}>
              Discard
            </Button>
          </Toolbar>
        </Card>
      ) : null}

      {data ? (
        <Split
          rail={
            <>
              <Card>
                <Section title="Why this number" note="Engine against served, and what it was made from.">
                  {selected && selectedRow ? (
                    <>
                      <div className={styles.railRow}>
                        <span className={styles.railLabel}>Who</span>
                        <span>{String(selectedRow.subject_name)}</span>
                      </div>
                      <div className={styles.railRow}>
                        <span className={styles.railLabel}>Field</span>
                        <span>{selected.field}</span>
                      </div>
                      <div className={styles.railRow}>
                        <span className={styles.railLabel}>The engine says</span>
                        <span>
                          {text((selectedRow.computed as Record<string, unknown>)[selected.field])}
                        </span>
                      </div>
                      <div className={styles.railRow}>
                        <span className={styles.railLabel}>Served</span>
                        <strong>
                          {text((selectedRow.shown as Record<string, unknown>)[selected.field])}
                        </strong>
                      </div>
                      <div className={styles.railRow}>
                        <span className={styles.railLabel}>Made from</span>
                        <span>{text(selectedRow.as_of_count)} completed matches</span>
                      </div>
                    </>
                  ) : (
                    <Empty title="Nothing selected" note="Click a number to see where it came from." />
                  )}
                </Section>
              </Card>

              <Card>
                <Section title="History" note="Every time this number moved, and why.">
                  {selectedHistory.length === 0 ? (
                    <Empty title="Nothing has moved" note="The engine's first answer is still standing." />
                  ) : (
                    selectedHistory.slice(0, 12).map((h, i) => (
                      <div key={i} className={styles.railRow}>
                        <span>
                          <span className={styles.railLabel}>{h.field}</span> {text(h.old)} →{" "}
                          {text(h.new)} <span className={styles.railLabel}>({h.cause})</span>
                        </span>
                        {h.cause === "edit" && h.editBatchId ? (
                          <Button disabled={busy} onClick={() => undo(h.editBatchId as string)}>
                            Undo
                          </Button>
                        ) : null}
                      </div>
                    ))
                  )}
                </Section>
              </Card>
            </>
          }
        >
          {data.rows.length === 0 ? (
            <Card>
              <Empty
                title="No numbers yet"
                note="Nothing has been computed for this scope. Run the stat from its Stats tab."
                action={<LinkButton to={`/${sport}/stats-engine`}>Go to Stats</LinkButton>}
              />
            </Card>
          ) : (
            <Table>
              <thead>
                <tr>
                  {data.gives === "table" ? <Th num>#</Th> : null}
                  <Th>Who</Th>
                  {columns.map((c) => (
                    <Th key={c} num title={data.fields[c]}>
                      {c}
                    </Th>
                  ))}
                  <Th>Counted</Th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => {
                  const rowId = Number(row.id);
                  const computed = row.computed as Record<string, unknown>;
                  const shown = row.shown as Record<string, unknown>;
                  const locked = Boolean(row.locked);
                  return (
                    <tr key={rowId} className={locked ? styles.locked : undefined}>
                      {data.gives === "table" ? <Td num>{text(row.rank)}</Td> : null}
                      <Td strong>
                        {String(row.subject_name)}
                        {locked ? (
                          <>
                            {" "}
                            <Pill tone="warn" dot>
                              locked
                            </Pill>
                          </>
                        ) : null}
                      </Td>
                      {columns.map((c) => {
                        const draft = draftFor(rowId, c);
                        const edited = JSON.stringify(shown?.[c]) !== JSON.stringify(computed?.[c]);
                        return (
                          <Td key={c} num onFocus={() => setSelected({ rowId, field: c })}>
                            <input
                              className={[
                                styles.cell,
                                draft ? styles.draft : edited ? styles.edited : styles.engine,
                              ].join(" ")}
                              defaultValue={text(shown?.[c])}
                              disabled={locked}
                              aria-label={`${String(row.subject_name)} ${c}`}
                              onChange={(e) => type(rowId, c, shown?.[c], e.target.value)}
                              onFocus={() => setSelected({ rowId, field: c })}
                            />
                            {edited && !draft ? (
                              <div className={styles.wasNote}>engine said {text(computed?.[c])}</div>
                            ) : null}
                          </Td>
                        );
                      })}
                      <Td quiet>{text(row.as_of_count)} matches</Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Split>
      ) : null}
    </Stack>
  );
}
